import React, { useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import { X, Check } from "lucide-react";
import { useI18n } from "../lib/i18n";

async function getCroppedBlob(imageSrc, pixelCrop) {
  const image = await new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = imageSrc;
  });
  const canvas = document.createElement("canvas");
  const size = Math.min(512, Math.max(pixelCrop.width, 96));
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(
    image,
    pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
    0, 0, size, size
  );
  return new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.9));
}

export default function AvatarCropModal({ open, imageSrc, onCancel, onConfirm }) {
  const { t } = useI18n();
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [pixels, setPixels] = useState(null);
  const [busy, setBusy] = useState(false);

  const onComplete = useCallback((_area, areaPixels) => setPixels(areaPixels), []);

  if (!open || !imageSrc) return null;

  const confirm = async () => {
    if (!pixels) return;
    setBusy(true);
    try {
      const blob = await getCroppedBlob(imageSrc, pixels);
      onConfirm(blob);
    } finally { setBusy(false); }
  };

  return (
    <div
      data-testid="crop-modal"
      className="fixed inset-0 bg-black/80 flex items-center justify-center p-4"
      style={{ zIndex: 10002 }}
      onClick={onCancel}
    >
      <div className="bg-white rounded-lg max-w-md w-full overflow-hidden border border-[#E6E2D8]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-[#E6E2D8]">
          <div>
            <div className="overline text-[#143122]">{t("crop_photo")}</div>
            <div className="font-serif-display text-xl text-[#143122]">{t("crop_preview")}</div>
          </div>
          <button data-testid="crop-cancel" onClick={onCancel} className="text-[#1A1F1C]/60 hover:text-[#143122]">
            <X size={20} strokeWidth={1.5} />
          </button>
        </div>

        <div className="relative bg-[#0d2016]" style={{ height: 320 }}>
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={1}
            cropShape="round"
            showGrid={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onComplete}
          />
        </div>

        <div className="p-4 space-y-4">
          <div>
            <label className="block text-xs font-medium mb-1.5">{t("zoom")}</label>
            <input
              data-testid="crop-zoom"
              type="range"
              min={1} max={3} step={0.05}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="w-full accent-[#143122]"
            />
          </div>
          <button
            data-testid="crop-confirm"
            onClick={confirm}
            disabled={busy || !pixels}
            className="kav-btn-primary w-full justify-center"
          >
            <Check size={14} strokeWidth={1.75} /> {busy ? "…" : t("use_photo")}
          </button>
        </div>
      </div>
    </div>
  );
}
